<template>
  <v-container class="py-6">
    <v-btn class="btn btn-sm mb-2">
      <router-link
        :to="'/pages/account/5d06629f4e20444ea87e12364c4fa9ef/all-clients'"
        >Back</router-link
      >
    </v-btn>
    <Alert v-if="showAlert" :text="alertMessage" :type="alertType" />
    <v-card outlined>
      <div v-if="loading" class="my-5 justify-center d-flex">
        <v-progress-circular class="px-8" indeterminate></v-progress-circular>
        <span>Loading client..</span>
      </div>
      <transition name="stf-fade">
        <div class="client-info" v-if="!loading">
          <v-toolbar-title class="mx-4 my-4 d-flex align-center">
            <b>
              <EditableField
                :value="clientPageData.name"
                keyPath="clientPageData.name"
                hideLabel
                :editing="editMode"
                @save="updateApiData"
              >
                <template #value>
                  <h2>{{ clientPageData.name }}</h2>
                </template>
              </EditableField>
            </b>
            <v-spacer></v-spacer>
            <v-btn
              small
              v-if="editMode"
              @click="cancelEdit"
              class="btn-sm float-sm-right mt-2 mx-2"
            >
              Cancel
            </v-btn>
            <v-btn
              icon
              small
              class="float-sm-right mt-2 mx-2 mr-6"
              @click="toggleEdit"
            >
              <v-icon>{{
                editMode ? 'mdi-content-save' : 'mdi-account-edit'
              }}</v-icon>
              {{ editMode ? 'Save' : 'Edit' }}
            </v-btn>
            <v-btn
              icon
              class="ml-2"
              @click="showDeleteDialog = true"
              color="error"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </v-toolbar-title>
          <v-dialog v-model="showDeleteDialog" max-width="400">
            <v-card>
              <v-card-title class="headline">Delete Client?</v-card-title>
              <v-card-text>
                Are you sure you want to delete this client? This action cannot
                be undone.
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  text
                  color="grey"
                  @click="showDeleteDialog = false"
                  :disabled="deletingClient"
                >
                  Cancel
                </v-btn>
                <v-btn
                  text
                  color="red"
                  @click="deleteClient"
                  :loading="deletingClient"
                  :disabled="deletingClient"
                >
                  {{ deletingClient ? 'Deleting...' : 'Delete' }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-divider></v-divider>
          <v-card-text>
            <v-row>
              <v-col cols="6">
                <v-list-item>
                  <EditableField
                    :value="clientPageData.email"
                    :keyPath="'clientPageData.email'"
                    label="Email"
                    :editing="editMode"
                    @save="updateApiData"
                  >
                    <template #label>
                      <v-list-item-title class="mb-0"
                        ><span class="font-weight-bold"
                          >Email</span
                        ></v-list-item-title
                      >
                    </template>
                  </EditableField>
                </v-list-item>
                <v-list-item>
                  <EditableField
                    :value="clientPageData.phone"
                    :keyPath="'clientPageData.phone'"
                    label="Phone"
                    :editing="editMode"
                    @save="updateApiData"
                  >
                    <template #label>
                      <v-list-item-title class="mb-0"
                        ><span class="font-weight-bold"
                          >Phone</span
                        ></v-list-item-title
                      >
                    </template>
                  </EditableField>
                </v-list-item>
              </v-col>
              <v-col cols="6">
                <v-list-item>
                  <EditableField
                    :value="clientPageData.address"
                    :keyPath="'clientPageData.address'"
                    label="Address"
                    :editing="editMode"
                    @save="updateApiData"
                  />
                </v-list-item>
                <v-list-item>
                  <EditableField
                    :value="clientPageData.status"
                    :keyPath="'clientPageData.status'"
                    label="Status"
                    :editing="editMode"
                    @save="updateApiData"
                  />
                </v-list-item>
              </v-col>
            </v-row>
          </v-card-text>
          <v-row>
            <v-col cols="6">
              <ClientRecentProjects
                :projects="clientPageData.recent_projects"
              />
            </v-col>
            <v-col cols="6">
              <NewClientContact
                v-if="creatingContact"
                :clientId="clientPageData.id"
                @contactCreated="
                  apiSetClientData()
                  creatingContact = false
                "
                @cancel="creatingContact = false"
              />
              <ClientContacts
                :creating="creatingContact"
                :contacts="clientPageData.contacts"
                @save="updateApiData"
                @createReq="creatingContact = true"
              />
              <NewClientDomain
                v-if="creatingDomain"
                :clientId="clientPageData.id"
                @domainCreated="
                  apiSetClientData()
                  creatingDomain = false
                "
                @cancel="creatingDomain = false"
              />
              <ClientDomains
                :creating="creatingDomain"
                :domains="clientPageData.domains"
                @save="updateApiData"
                @createReq="creatingDomain = true"
              />
              <NewClientService
                v-if="creatingService"
                :clientId="clientPageData.id"
                @serviceCreated="
                  apiSetClientData()
                  creatingService = false
                "
                @cancel="creatingService = false"
              />
              <ClientServices
                :creating="creatingService"
                :services="clientPageData.services"
                @save="updateApiData"
                @createReq="creatingService = true"
              />
            </v-col>
          </v-row>
        </div>
      </transition>
    </v-card>
  </v-container>
</template>

<script>
  import Alert from '@/components/Alert.vue'
  import ClientContacts from '../Components/ClientContacts.vue'
  import ClientDomains from '../Components/ClientDomains.vue'
  import ClientServices from '../Components/ClientServices.vue'
  import NewClientDomain from '../Components/NewClientDomain.vue'
  import NewClientContact from '../Components/NewClientContact.vue'
  import NewClientService from '../Components/NewClientService.vue'
  import ClientRecentProjects from '../Components/ClientRecentProjects.vue'
  import EditableField from '../../components/EditableField.vue'
  import { setNestedValue } from '@/util/helpers'

  export default {
    name: 'ClientDetails',
    props: ['clientId'],
    components: {
      Alert,
      NewClientContact,
      NewClientDomain,
      NewClientService,
      ClientContacts,
      ClientDomains,
      ClientServices,
      ClientRecentProjects,
      EditableField,
    },
    data() {
      return {
        client: {},
        clientPageData: {},
        creatingContact: false,
        creatingDomain: false,
        creatingService: false,
        loading: true,
        editMode: false,
        showAlert: false,
        showDeleteDialog: false,
        alertMessage: '',
        alertType: 'success',
        deletingClient: false,
      }
    },
    mounted() {
      this.apiSetClientData()
    },
    watch: {
      client: {
        immediate: true,
        handler(newVal) {
          this.clientPageData = JSON.parse(JSON.stringify(newVal))
        },
      },
    },
    methods: {
      toggleEdit() {
        if (this.editMode) {
          setTimeout(() => {
            let details = { ...this.clientPageData }
            for (let key of [
              'created_at',
              'updated_at',
              'deleted_at',
              'id',
              'account_id',
              'domains',
              'contacts',
              'services',
              'recent_projects',
            ])
              delete details[key]
            this.apiModifyClient(this.$route.params.clientId, details)
              .then(response => {
                this.client = response
                this.clientPageData = JSON.parse(JSON.stringify(response))
                this.alertMessage = 'Client data updated successfully'
                this.alertType = 'success'
                this.showAlert = true
              })
              .catch(error => {
                console.log(error)
              })
          }, 200)
        }
        this.editMode = !this.editMode
      },
      cancelEdit() {
        this.clientPageData = JSON.parse(JSON.stringify(this.client))
        this.editMode = false
      },
      apiSetClientData() {
        this.apiGetClientData(this.$route.params.clientId)
          .then(data => {
            this.client = data
            this.loading = false
          })
          .catch(error => {
            this.mainStateSet('projectData', [])
            this.consoleLog(
              'error',
              'mainMixin.js fn(apiSetProjectData): error catch',
              error
            )
          })
      },
      updateApiData(details) {
        setNestedValue(this, details.keyPath, details.value)
      },
      deleteClient() {
        this.deletingClient = true
        const payload = {
          id: this.$route.params.clientId,
        }

        this.apiDeleteClient(this.$route.params.clientId)
          .then(response => {
            if (response) {
              this.alertMessage = 'Client deleted successfully'
              this.alertType = 'success'
              this.showAlert = true
              this.showDeleteDialog = false
              this.deletingClient = false
              setTimeout(() => {
                this.$router.push(
                  '/pages/account/5d06629f4e20444ea87e12364c4fa9ef/all-clients'
                )
              }, 1500)
            } else {
              console.error('Failed to delete client:', response)
              this.alertMessage =
                'Failed to delete client: ' +
                (response.message || 'Unknown error')
              this.alertType = 'error'
              this.showAlert = true
              this.deletingClient = false
            }
          })
          .catch(error => {
            console.error('Error deleting client:', error)
            this.alertMessage =
              'Error deleting client: ' + (error.message || 'Network error')
            this.alertType = 'error'
            this.showAlert = true
            this.showDeleteDialog = false
            this.deletingClient = false
          })
      },
    },
  }
</script>

<style scoped></style>
